/**
 *  @class
 *  @function ImageHotspots
 *
 *  On a computer a pin opens its popup beside itself; another pin, a tap or click anywhere else, or
 *  Escape closes it. On a phone the picture is too small to cover, so the pins choose which
 *  description shows under it, starting on the first, and there is always one showing.
 */
if (!customElements.get('image-hotspots')) {
  class ImageHotspots extends HTMLElement {
    constructor() {
      super();

      this.dots = [...this.querySelectorAll('.image-hotspots--pin')];
      this.buttons = this.querySelectorAll('.image-hotspots--pin-button');
      this.details = [...(this.closest('.image-hotspots') || this).querySelectorAll('.image-hotspots--detail')];
      this.phone = window.matchMedia('(max-width: 767px)');
      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';

      this.onOutside = this.onOutside.bind(this);
      this.onKey = this.onKey.bind(this);
      this.rest = this.rest.bind(this);

      this.dots.forEach((dot, index) => {
        dot.querySelector('.image-hotspots--pin-button').addEventListener('click', () => this.onClick(index));
      });
    }
    connectedCallback() {
      if (this.animations_enabled) {
        this.prepareAnimations();
      }
      this.rest();
      this.phone.addEventListener('change', this.rest);
      document.addEventListener('pointerdown', this.onOutside);
      document.addEventListener('keydown', this.onKey);
    }
    disconnectedCallback() {
      this.phone.removeEventListener('change', this.rest);
      document.removeEventListener('pointerdown', this.onOutside);
      document.removeEventListener('keydown', this.onKey);
    }
    rest() {
      this.dots.forEach((dot, index) => this.setActive(index, false));
      if (this.phone.matches && this.dots.length) {
        this.setActive(0, true);
      }
    }
    setActive(index, active) {
      const dot = this.dots[index];
      dot.classList.toggle('active', active);
      dot.querySelector('.image-hotspots--pin-button').setAttribute('aria-expanded', active ? 'true' : 'false');
      if (this.details[index]) {
        this.details[index].classList.toggle('active', active);
      }
    }
    onClick(index) {
      const dot = this.dots[index];
      if (this.phone.matches) {
        this.dots.forEach((other, i) => this.setActive(i, i === index));
        return;
      }
      if (dot.classList.contains('active')) {
        this.setActive(index, false);
        return;
      }
      this.dots.forEach((other, i) => {
        if (i !== index) this.setActive(i, false);
      });
      this.checkCardPosition(dot);
      this.setActive(index, true);
    }
    onOutside(event) {
      if (this.phone.matches) return;
      if (this.dots.some((dot) => dot.contains(event.target))) return;
      this.dots.forEach((dot, index) => {
        if (dot.classList.contains('active')) this.setActive(index, false);
      });
    }
    onKey(event) {
      if (event.key !== 'Escape' || this.phone.matches) return;
      const index = this.dots.findIndex((dot) => dot.classList.contains('active'));
      if (index === -1) return;
      this.setActive(index, false);
      this.dots[index].querySelector('.image-hotspots--pin-button').focus();
    }
    prepareAnimations() {
      let section = this;

      this.tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top 50%"
        }
      });

      this.tl
        .fromTo(this.buttons, {
          scale: 0
        }, {
          duration: 2,
          stagger: {
            each: 0.2,
            onComplete() {
              this.targets()[0].classList.add('hotspot--pinned');
            }
          },
          scale: 1,
          ease: 'elastic.out(1.2, 0.5)',
        });
    }
    // Measured on every open, from the popup's own place: it opens upward when it would run past the
    // picture or the screen, and slides sideways to stay inside the picture.
    checkCardPosition(dot) {
      dot.classList.remove('bottom-dot');
      dot.style.setProperty('--content-offset', '0px');
      let dotBounds = dot.querySelector('.image-hotspots--pin-bubble').getBoundingClientRect(),
        imageBounds = this.getBoundingClientRect();

      if (dotBounds.bottom > document.documentElement.clientHeight || dotBounds.bottom > imageBounds.bottom) {
        dot.classList.add('bottom-dot');
      }
      if (dotBounds.right > imageBounds.right) {
        dot.style.setProperty('--content-offset', `${imageBounds.right - dotBounds.right - 30}px`);
      } else if (dotBounds.left < imageBounds.left) {
        dot.style.setProperty('--content-offset', `${imageBounds.left - dotBounds.left + 30}px`);
      }
    }
  }
  customElements.define('image-hotspots', ImageHotspots);
}
